import { B as validateProjectName, F as normalizeCreateCommandOptions, M as CREATE_LLM_PROVIDERS, O as writeObservabilityEnv, a as getPackageManager, c as isGitInitialized, r as getAnalytics, s as gitInit, z as validateCreateOptionConflicts } from "./analytics-Buqi-VCs.js";
import { o as platformFetch, r as authHeaders, t as MASTRA_PLATFORM_API_URL } from "./client-Bdk2AMV6.js";
import { i as getToken, t as LoginCancelledError } from "./credentials-CdxijtI9.js";
import { r as resolveCurrentOrg, t as OrgSelectionCancelledError } from "./orgs-Dj0nij3E.js";
import color from "picocolors";
import { randomUUID } from "crypto";
import fs, { constants } from "fs";
import path from "path";
import fsPromises from "fs/promises";
import { execa } from "execa";
import * as p from "@clack/prompts";
import yoctoSpinner from "yocto-spinner";
import semver from "semver";
//#region src/utils/clone-template.ts
const INTERRUPT_SIGNALS = ["SIGINT", "SIGTERM"];
function startSpinner(text, signal, silent = false) {
	if (silent) return void 0;
	if (!signal) return yoctoSpinner({ text }).start();
	const existingListeners = new Map(INTERRUPT_SIGNALS.map((interruptSignal) => [interruptSignal, new Set(process.listeners(interruptSignal))]));
	const spinner = yoctoSpinner({ text }).start();
	for (const interruptSignal of INTERRUPT_SIGNALS) for (const listener of process.listeners(interruptSignal)) if (!existingListeners.get(interruptSignal)?.has(listener)) process.removeListener(interruptSignal, listener);
	return spinner;
}
async function cloneTemplate(options) {
	const { template, projectName, targetDir, branch, signal, silent = false } = options;
	const projectPath = targetDir ? path.resolve(targetDir, projectName) : path.resolve(projectName);
	const spinner = startSpinner(`Cloning template "${template.title}"...`, signal, silent);
	let ownsProjectPath = false;
	try {
		if (await directoryExists(projectPath)) {
			spinner?.error(`Directory ${projectName} already exists`);
			throw new Error(`Directory ${projectName} already exists`);
		}
		ownsProjectPath = true;
		await cloneRepositoryWithoutGit(template.githubUrl, projectPath, branch, signal);
		await updatePackageJson(projectPath, projectName);
		spinner?.success(`Template "${template.title}" cloned successfully to ${projectName}`);
		return projectPath;
	} catch (error) {
		if (ownsProjectPath) await fsPromises.rm(projectPath, {
			recursive: true,
			force: true
		});
		spinner?.error(`Failed to clone template: ${error instanceof Error ? error.message : "Unknown error"}`);
		throw error;
	}
}
async function directoryExists(dirPath) {
	try {
		return (await fsPromises.stat(dirPath)).isDirectory();
	} catch {
		return false;
	}
}
async function cloneRepositoryWithoutGit(repoUrl, targetPath, branch, signal) {
	signal?.throwIfAborted();
	try {
		const degitRepo = repoUrl.replace("https://github.com/", "");
		await execa("npx", [
			"degit",
			branch ? `${degitRepo}#${branch}` : degitRepo,
			targetPath
		], {
			cwd: process.cwd(),
			...signal ? { cancelSignal: signal } : {}
		});
		signal?.throwIfAborted();
		if ((await fsPromises.readdir(targetPath)).length === 0) throw new Error("degit completed without cloning template files");
	} catch {
		if (signal?.aborted) signal.throwIfAborted();
		await fsPromises.rm(targetPath, {
			recursive: true,
			force: true
		});
		try {
			const gitArgs = ["clone"];
			if (branch) gitArgs.push("--branch", branch);
			gitArgs.push(repoUrl, targetPath);
			await execa("git", gitArgs, {
				cwd: process.cwd(),
				...signal ? { cancelSignal: signal } : {}
			});
			signal?.throwIfAborted();
			const gitDir = path.join(targetPath, ".git");
			if (await directoryExists(gitDir)) await fsPromises.rm(gitDir, {
				recursive: true,
				force: true
			});
		} catch (gitError) {
			if (signal?.aborted) signal.throwIfAborted();
			throw new Error(`Failed to clone repository: ${gitError instanceof Error ? gitError.message : "Unknown error"}`);
		}
	}
}
async function updatePackageJson(projectPath, projectName) {
	const packageJsonPath = path.join(projectPath, "package.json");
	try {
		const packageJsonContent = await fsPromises.readFile(packageJsonPath, "utf-8");
		const packageJson = JSON.parse(packageJsonContent);
		packageJson.name = projectName;
		await fsPromises.writeFile(packageJsonPath, JSON.stringify(packageJson, null, 2), "utf-8");
	} catch (error) {
		p.log.warn(`Could not update package.json: ${error instanceof Error ? error.message : String(error)}`);
	}
}
async function installDependencies(projectPath, packageManager, timeout, signal, silent = false) {
	const spinner = startSpinner("Installing dependencies...", signal, silent);
	try {
		await execa(packageManager || getPackageManager(), ["install"], {
			cwd: projectPath,
			timeout,
			killSignal: "SIGTERM",
			...signal ? { cancelSignal: signal } : {}
		});
		spinner?.success("Dependencies installed successfully");
	} catch (error) {
		spinner?.error(`Failed to install dependencies: ${error instanceof Error ? error.message : "Unknown error"}`);
		throw error;
	}
}
//#endregion
//#region src/utils/template-utils.ts
const TEMPLATES_API_URL = process.env.MASTRA_TEMPLATES_API_URL || "https://mastra.ai/api/templates.json";
async function loadTemplates() {
	try {
		const response = await fetch(TEMPLATES_API_URL);
		if (!response.ok) throw new Error(`Failed to fetch templates: ${response.statusText}`);
		return await response.json();
	} catch (error) {
		console.error("Error loading templates:", error);
		throw new Error("Failed to load templates. Please check your internet connection and try again.");
	}
}
function pluralize(count, singular, plural) {
	return count === 1 ? singular : plural || `${singular}s`;
}
async function selectTemplate(templates, options = {}) {
	const choices = templates.map((template) => {
		const parts = [];
		if (template.agents?.length) parts.push(`${template.agents.length} ${pluralize(template.agents.length, "agent")}`);
		if (template.tools?.length) parts.push(`${template.tools.length} ${pluralize(template.tools.length, "tool")}`);
		if (template.workflows?.length) parts.push(`${template.workflows.length} ${pluralize(template.workflows.length, "workflow")}`);
		if (template.mcp?.length) parts.push(`${template.mcp.length} ${pluralize(template.mcp.length, "MCP server")}`);
		if (template.networks?.length) parts.push(`${template.networks.length} ${pluralize(template.networks.length, "agent network")}`);
		return {
			value: template,
			label: template.title,
			hint: parts.join(", ") || "Template components"
		};
	});
	const selected = await p.select({
		message: "Select a template:",
		options: choices,
		...options.signal ? { signal: options.signal } : {}
	});
	if (p.isCancel(selected)) return null;
	return selected;
}
function findTemplateByName(templates, templateName) {
	let template = templates.find((t) => t.slug === templateName);
	if (template) return template;
	const slugWithPrefix = `template-${templateName}`;
	template = templates.find((t) => t.slug === slugWithPrefix);
	if (template) return template;
	template = templates.find((t) => t.title.toLowerCase() === templateName.toLowerCase());
	if (template) return template;
	return null;
}
//#endregion
//#region src/commands/init/observability-provision.ts
const DEFAULT_PLATFORM_API_URL = "https://platform.mastra.ai";
/**
* Walk the user through enabling Mastra Observability for a freshly-scaffolded
* project: log in (via the existing browser flow) if needed, pick or create a
* platform project, mint a fresh org-scoped ingest token, and return what the
* caller needs to write to `.env`.
*
* Defaults the project name to `defaultProjectName` (typically the package
* name from `package.json`) when creating a new project.
*/
async function provisionObservabilityProject({ defaultProjectName, observabilityProject, mode = "pick", token: providedToken, org } = {}) {
	const token = providedToken ?? await getToken();
	const { orgId, orgName } = org ?? await resolveCurrentOrg(token, { forcePrompt: true });
	let project;
	if (observabilityProject) {
		const match = (await listProjects(token, orgId)).find((proj) => proj.name === observabilityProject || proj.slug === observabilityProject);
		if (match) project = match;
		else project = await createProjectByName({
			token,
			orgId,
			name: observabilityProject
		});
	} else if (mode === "create") {
		if (!defaultProjectName) throw new Error("defaultProjectName is required when mode is \"create\"");
		project = await createProjectByName({
			token,
			orgId,
			name: defaultProjectName
		});
	} else {
		const projects = await listProjects(token, orgId);
		if (projects.length === 0) project = await createProject({
			token,
			orgId,
			defaultName: defaultProjectName,
			orgName
		});
		else {
			const choice = await p.select({
				message: `Select a project (in ${orgName})`,
				options: [...projects.map((proj) => ({
					value: proj.id,
					label: proj.name,
					hint: proj.slug
				})), {
					value: "__new__",
					label: "+ Create new project"
				}]
			});
			if (p.isCancel(choice)) throw new Error("Cancelled");
			if (choice === "__new__") project = await createProject({
				token,
				orgId,
				defaultName: defaultProjectName,
				orgName
			});
			else project = projects.find((proj) => proj.id === choice);
		}
	}
	const result = {
		token: await mintOrgToken({
			token,
			orgId,
			keyName: `mastra observability – ${project.name}`
		}),
		projectId: project.id,
		projectSlug: project.slug,
		projectName: project.name,
		orgName
	};
	if (MASTRA_PLATFORM_API_URL !== DEFAULT_PLATFORM_API_URL) result.tracesEndpoint = deriveTracesEndpoint(MASTRA_PLATFORM_API_URL, project.id);
	return result;
}
async function listProjects(token, orgId) {
	const res = await platformFetch(`${MASTRA_PLATFORM_API_URL}/v1/studio/projects`, { headers: authHeaders(token, orgId) });
	if (!res.ok) throw new Error(`Failed to list projects (${res.status})`);
	return (await res.json()).projects;
}
async function createProject({ token, orgId, defaultName, orgName }) {
	const name = await p.text({
		message: `New project name (in ${orgName})`,
		placeholder: defaultName ?? "my-mastra-app",
		defaultValue: defaultName,
		validate: (v) => !v || v.trim().length === 0 ? "Name is required" : void 0
	});
	if (p.isCancel(name)) throw new Error("Cancelled");
	return createProjectByName({
		token,
		orgId,
		name
	});
}
async function createProjectByName({ token, orgId, name }) {
	const res = await platformFetch(`${MASTRA_PLATFORM_API_URL}/v1/studio/projects`, {
		method: "POST",
		headers: {
			...authHeaders(token, orgId),
			"Content-Type": "application/json"
		},
		body: JSON.stringify({
			name,
			studioEnabled: false,
			serverEnabled: false
		})
	});
	if (!res.ok) throw new Error(`Failed to create project (${res.status})`);
	return (await res.json()).project;
}
async function mintOrgToken({ token, orgId, keyName }) {
	const res = await platformFetch(`${MASTRA_PLATFORM_API_URL}/v1/auth/tokens`, {
		method: "POST",
		headers: {
			...authHeaders(token, orgId),
			"Content-Type": "application/json"
		},
		body: JSON.stringify({ name: keyName })
	});
	if (!res.ok) throw new Error(`Failed to create access token (${res.status})`);
	return (await res.json()).secret;
}
/**
* Derive a per-project spans endpoint matching the mobs-collector route
* `POST /projects/:projectId/ai/spans/publish`. Only used when a non-default
* platform URL is in play; production usage relies on the
* MastraPlatformExporter's own default base.
*/
function deriveTracesEndpoint(platformUrl, projectId) {
	return `${new URL(platformUrl).origin}/projects/${projectId}/ai/spans/publish`;
}
//#endregion
//#region src/commands/init/skills-install.ts
async function installMastraSkills({ directory, agents }) {
	try {
		await execa("npx", [
			"skills",
			"add",
			"mastra-ai/skills",
			"--agent",
			...agents,
			"-y"
		], {
			cwd: directory,
			stdio: "pipe"
		});
		return {
			success: true,
			agents
		};
	} catch (error) {
		return {
			success: false,
			error: error instanceof Error ? error.message : "Unknown error",
			agents
		};
	}
}
//#endregion
//#region src/commands/create/coding-agents.ts
const EXECUTABLE_SKILLS = [
	["claude", "claude-code"],
	["droid", "droid"],
	["pi", "pi"],
	["codex", "universal"],
	["cursor-agent", "universal"],
	["gemini", "universal"],
	["opencode", "universal"]
];
const DEFAULT_WINDOWS_PATHEXT = ".COM;.EXE;.BAT;.CMD";
async function detectCodingAgentSkills({ env = process.env, platform = process.platform } = {}) {
	const pathDirectories = (env.PATH ?? "").split(platform === "win32" ? ";" : path.delimiter).map((directory) => directory.trim()).filter(Boolean);
	if (pathDirectories.length === 0) return [["", "universal"]];
	const windowsExtensions = platform === "win32" ? parseWindowsExtensions(env.PATHEXT) : [];
	const detected = [];
	for (const [executable, skill] of EXECUTABLE_SKILLS) if (await hasExecutable({
		executable,
		pathDirectories,
		platform,
		windowsExtensions
	}) && !detected.some(([, s]) => s === skill)) detected.push([executable, skill]);
	return detected.length > 0 ? detected : [["", "universal"]];
}
function parseWindowsExtensions(pathExt) {
	const value = pathExt?.trim() ? pathExt : DEFAULT_WINDOWS_PATHEXT;
	const extensions = [];
	for (const entry of value.split(";")) {
		const trimmed = entry.trim();
		if (!trimmed) continue;
		const normalized = `${trimmed.startsWith(".") ? "" : "."}${trimmed}`.toUpperCase();
		if (!extensions.includes(normalized)) extensions.push(normalized);
	}
	return extensions.length > 0 ? extensions : DEFAULT_WINDOWS_PATHEXT.split(";");
}
async function hasExecutable({ executable, pathDirectories, platform, windowsExtensions }) {
	for (const directory of pathDirectories) if (platform === "win32") {
		if (await hasWindowsExecutable(directory, executable, windowsExtensions)) return true;
	} else if (await hasPosixExecutable(directory, executable)) return true;
	return false;
}
async function hasPosixExecutable(directory, executable) {
	const candidate = path.join(directory, executable);
	try {
		if (!(await fsPromises.stat(candidate)).isFile()) return false;
		await fsPromises.access(candidate, constants.X_OK);
		return true;
	} catch {
		return false;
	}
}
async function hasWindowsExecutable(directory, executable, extensions) {
	try {
		const entries = await fsPromises.readdir(directory, { withFileTypes: true });
		const candidates = new Set(extensions.map((extension) => `${executable}${extension}`.toLowerCase()));
		return entries.some((entry) => entry.isFile() && candidates.has(entry.name.toLowerCase()));
	} catch {
		return false;
	}
}
//#endregion
//#region src/commands/create/version-resolver.ts
/**
* Resolve the exact published version for each requested Mastra package via its
* `dist-tags.<versionTag>` entry. Returns a complete map of exact versions only
* when every package resolves successfully; otherwise returns `undefined`.
*
* The low-level resolver never logs — the calling boundary owns the fallback
* warning.
*/
async function resolveMastraPackageVersions(packageNames, versionTag) {
	const uniqueNames = [...new Set(packageNames)].sort();
	if (uniqueNames.length === 0) return {};
	const resolved = {};
	for (const packageName of uniqueNames) {
		const result = await resolveSinglePackageVersion(packageName, versionTag);
		if (result === void 0) return void 0;
		resolved[packageName] = result;
	}
	return resolved;
}
const NPM_VIEW_TIMEOUT_MS = 15e3;
async function resolveSinglePackageVersion(packageName, versionTag) {
	let stdout;
	try {
		const { stdout: output } = await execa("npm", [
			"view",
			packageName,
			`dist-tags.${versionTag}`
		], { timeout: NPM_VIEW_TIMEOUT_MS });
		stdout = output;
	} catch {
		return;
	}
	const trimmed = stdout.trim();
	if (!trimmed) return void 0;
	if (trimmed.includes("\n")) return void 0;
	const normalized = semver.valid(trimmed);
	if (normalized === null || normalized !== trimmed) return void 0;
	return normalized;
}
//#endregion
//#region src/commands/create/utils.ts
const EMPTY_TSCONFIG = {
	compilerOptions: {
		target: "ES2022",
		module: "ES2022",
		moduleResolution: "bundler",
		esModuleInterop: true,
		forceConsistentCasingInFileNames: true,
		strict: true,
		skipLibCheck: true,
		noEmit: true,
		outDir: "dist"
	},
	include: ["src/**/*"]
};
const EMPTY_GITIGNORE = `node_modules
dist
.mastra
.env
.env.*
*.db
*.db-*
*.duckdb
*.duckdb.wal
`;
const PNPM_WORKSPACE = `packages:
  - '.'
minimumReleaseAgeExclude:
  - mastra
  - "@mastra/*"
allowBuilds:
  bufferutil: true
  esbuild: true
  protobufjs: true
  sharp: true
  utf-8-validate: true
`;
async function createOwnedStagingDirectory(invocationCwd, projectName) {
	const rootPath = await fsPromises.mkdtemp(path.join(invocationCwd, `.${projectName}.mastra-create-`));
	return {
		rootPath,
		projectPath: path.join(rootPath, projectName)
	};
}
async function cleanupOwnedStagingDirectory(rootPath) {
	await fsPromises.rm(rootPath, {
		recursive: true,
		force: true
	});
}
function existingTargetError(projectName) {
	return /* @__PURE__ */ new Error(`A file or directory named "${projectName}" already exists. Please choose a different name.`);
}
async function publishStagedProject({ projectPath, targetPath, projectName }) {
	if (fs.existsSync(targetPath)) throw existingTargetError(projectName);
	try {
		await fsPromises.rename(projectPath, targetPath);
	} catch (error) {
		if (fs.existsSync(targetPath)) throw existingTargetError(projectName);
		throw error;
	}
}
async function writeEmptyScaffold({ projectPath, projectName, versionTag, packageManager }) {
	await fsPromises.mkdir(path.join(projectPath, "src/mastra"), { recursive: true });
	const resolved = await resolveMastraPackageVersions(["@mastra/core", "mastra"], versionTag);
	if (resolved === void 0) console.warn(`We could not resolve exact Mastra package versions for the "${versionTag}" channel, using the channel tag instead`);
	const coreVersion = resolved?.["@mastra/core"] ?? versionTag;
	const mastraVersion = resolved?.mastra ?? versionTag;
	const packageJson = {
		name: projectName,
		version: "1.0.0",
		private: true,
		type: "module",
		engines: { node: ">=22.13.0" },
		scripts: {
			dev: "mastra dev",
			build: "mastra build",
			start: "mastra start"
		},
		dependencies: { "@mastra/core": coreVersion },
		devDependencies: {
			mastra: mastraVersion,
			typescript: "^6.0.3",
			"@types/node": "latest"
		}
	};
	const writes = [
		fsPromises.writeFile(path.join(projectPath, "package.json"), `${JSON.stringify(packageJson, null, 2)}\n`, "utf8"),
		fsPromises.writeFile(path.join(projectPath, "tsconfig.json"), `${JSON.stringify(EMPTY_TSCONFIG, null, 2)}\n`, "utf8"),
		fsPromises.writeFile(path.join(projectPath, ".gitignore"), EMPTY_GITIGNORE, "utf8"),
		fsPromises.writeFile(path.join(projectPath, "src/mastra/index.ts"), "import { Mastra } from '@mastra/core/mastra';\n\nexport const mastra = new Mastra({});\n", "utf8")
	];
	if (packageManager === "pnpm") writes.push(fsPromises.writeFile(path.join(projectPath, "pnpm-workspace.yaml"), PNPM_WORKSPACE, "utf8"));
	await Promise.all(writes);
}
//#endregion
//#region src/commands/create/provider-adapter.ts
const MANAGED_PROVIDER_CONFIGS = {
	openai: {
		displayName: "OpenAI",
		apiKeyEnv: "OPENAI_API_KEY"
	},
	anthropic: {
		displayName: "Anthropic",
		primaryModel: "anthropic/claude-sonnet-5",
		observationalModel: "anthropic/claude-haiku-4-5",
		apiKeyEnv: "ANTHROPIC_API_KEY"
	},
	google: {
		displayName: "Google Gemini",
		primaryModel: "google/gemini-3.5-flash",
		observationalModel: "google/gemini-3.5-flash",
		apiKeyEnv: "GOOGLE_GENERATIVE_AI_API_KEY"
	},
	xai: {
		displayName: "xAI",
		primaryModel: "xai/grok-4.3",
		observationalModel: "xai/grok-4.3",
		apiKeyEnv: "XAI_API_KEY"
	}
};
const OPENAI_API_KEY = "OPENAI_API_KEY";
const PRIMARY_OPENAI_MODEL = /(\bmodel\s*:\s*['"])openai\/[^'"]+(['"])(?=\s*,?\s*\n\s*defaultOptions\s*:)/g;
const OBSERVATIONAL_OPENAI_MODEL = /(observationalMemory\s*:\s*\{[^{}]*?\bmodel\s*:\s*['"])openai\/[^'"]+(['"])/g;
function findMatches(content, pattern) {
	const flags = pattern.flags.includes("g") ? pattern.flags : `${pattern.flags}g`;
	return [...content.matchAll(new RegExp(pattern.source, flags))];
}
function replaceSingleMatch(content, pattern, replacement) {
	if (findMatches(content, pattern).length !== 1) return {
		applied: false,
		content
	};
	if (typeof replacement === "string") return {
		applied: true,
		content: content.replace(pattern, replacement)
	};
	return {
		applied: true,
		content: content.replace(pattern, replacement)
	};
}
function getDependencyMap(manifest, section) {
	const value = manifest[section];
	if (value === void 0 && section === "devDependencies") return void 0;
	if (!value || typeof value !== "object" || Array.isArray(value)) return void 0;
	return value;
}
function collectMastraPackages(manifestSource) {
	let manifest;
	try {
		manifest = JSON.parse(manifestSource);
	} catch {
		return [];
	}
	const names = /* @__PURE__ */ new Set();
	for (const section of ["dependencies", "devDependencies"]) {
		const deps = manifest[section];
		if (deps && typeof deps === "object" && !Array.isArray(deps)) {
			for (const name of Object.keys(deps)) if (name === "mastra" || name.startsWith("@mastra/")) names.add(name);
		}
	}
	return [...names];
}
function normalizeManagedManifest(content, mastraVersions, fallbackTag) {
	let manifest;
	try {
		manifest = JSON.parse(content);
	} catch {
		return {
			applied: false,
			content
		};
	}
	const dependencies = getDependencyMap(manifest, "dependencies");
	const devDependencies = getDependencyMap(manifest, "devDependencies");
	if (!dependencies || manifest.devDependencies !== void 0 && !devDependencies) return {
		applied: false,
		content
	};
	const dependencySections = [dependencies, devDependencies].filter((section) => section !== void 0);
	for (const section of dependencySections) for (const packageName of Object.keys(section)) if (packageName === "mastra" || packageName.startsWith("@mastra/")) section[packageName] = mastraVersions?.[packageName] ?? fallbackTag;
	return {
		applied: true,
		content: `${JSON.stringify(manifest, null, 2)}\n`
	};
}
function adaptAgentSource(source, provider, config) {
	if (provider === "openai") return {
		applied: true,
		content: source
	};
	if (!config.primaryModel || !config.observationalModel) return {
		applied: false,
		content: source
	};
	if (findMatches(source, PRIMARY_OPENAI_MODEL).length !== 1) return {
		applied: false,
		content: source
	};
	if (findMatches(source, OBSERVATIONAL_OPENAI_MODEL).length !== 1) return {
		applied: false,
		content: source
	};
	return {
		applied: true,
		content: source.replace(PRIMARY_OPENAI_MODEL, `$1${config.primaryModel}$2`).replace(OBSERVATIONAL_OPENAI_MODEL, `$1${config.observationalModel}$2`)
	};
}
function replaceEnvKey(source, nextKey) {
	return replaceSingleMatch(source, new RegExp(`^([ \\t]*)${OPENAI_API_KEY}[ \\t]*=.*$`, "m"), (_line, indentation) => `${indentation}${nextKey}=`);
}
function setEnvValue(source, key, value) {
	return replaceSingleMatch(source, new RegExp(`^([ \\t]*)${key}[ \\t]*=.*$`, "m"), (_line, indentation) => `${indentation}${key}=${value}`);
}
function adaptReadme(source, provider, config, projectName, packageManager) {
	if (!(source.includes(OPENAI_API_KEY) || /^([ \t]*)npx create-mastra@\S+.*$/m.test(source))) return {
		applied: false,
		content: source
	};
	return {
		applied: true,
		content: source.replace(/^# .+$/m, `# ${projectName}`).replaceAll("npm run dev", `${packageManager} run dev`).replace(/^([ \t]*)npx create-mastra@\S+.*$/m, `$1npx create-mastra@latest <project-name> --llm ${provider}`).replaceAll(OPENAI_API_KEY, config.apiKeyEnv)
	};
}
async function writeSecureEnv(envPath, content) {
	const tempPath = path.join(path.dirname(envPath), `.env.mastra-create-${process.pid}-${randomUUID()}.tmp`);
	try {
		await fsPromises.writeFile(tempPath, content, {
			encoding: "utf8",
			flag: "wx",
			mode: 384
		});
		if (process.platform !== "win32") await fsPromises.chmod(tempPath, 384);
		await fsPromises.rename(tempPath, envPath);
		return true;
	} catch {
		try {
			await fsPromises.rm(tempPath, { force: true });
		} catch {}
		return false;
	}
}
async function adaptDefaultTemplate({ projectPath, projectName, packageManager, provider, apiKey, versionTag }) {
	const config = MANAGED_PROVIDER_CONFIGS[provider];
	let adaptationFailed = false;
	const write = async (filePath, content) => {
		try {
			await fsPromises.writeFile(filePath, content, "utf8");
			return true;
		} catch {
			adaptationFailed = true;
			return false;
		}
	};
	const agentPath = path.join(projectPath, "src/mastra/agents/agent.ts");
	if (provider !== "openai") try {
		const result = adaptAgentSource(await fsPromises.readFile(agentPath, "utf8"), provider, config);
		if (!result.applied) adaptationFailed = true;
		else await write(agentPath, result.content);
	} catch {
		adaptationFailed = true;
	}
	const packageJsonPath = path.join(projectPath, "package.json");
	try {
		const source = await fsPromises.readFile(packageJsonPath, "utf8");
		const mastraPackages = collectMastraPackages(source);
		const resolvedVersions = await resolveMastraPackageVersions(mastraPackages, versionTag);
		if (resolvedVersions === void 0 && mastraPackages.length > 0) console.warn(`We could not resolve exact Mastra package versions for the "${versionTag}" channel, using the channel tag instead`);
		const result = normalizeManagedManifest(source, resolvedVersions, versionTag);
		if (!result.applied) adaptationFailed = true;
		else await write(packageJsonPath, result.content);
	} catch {
		adaptationFailed = true;
	}
	const envExamplePath = path.join(projectPath, ".env.example");
	const envPath = path.join(projectPath, ".env");
	let adaptedEnvExample;
	try {
		const result = replaceEnvKey(await fsPromises.readFile(envExamplePath, "utf8"), config.apiKeyEnv);
		if (!result.applied) adaptationFailed = true;
		else if (await write(envExamplePath, result.content)) adaptedEnvExample = result.content;
	} catch {
		adaptationFailed = true;
	}
	let apiKeyWritten = false;
	if (adaptedEnvExample === void 0) {
		if (apiKey) adaptationFailed = true;
	} else if (apiKey) {
		const result = setEnvValue(adaptedEnvExample, config.apiKeyEnv, apiKey);
		if (!result.applied) adaptationFailed = true;
		else {
			apiKeyWritten = await writeSecureEnv(envPath, result.content);
			if (!apiKeyWritten) adaptationFailed = true;
		}
	} else try {
		await fsPromises.rm(envPath, { force: true });
	} catch {
		adaptationFailed = true;
	}
	const readmePath = path.join(projectPath, "README.md");
	try {
		const result = adaptReadme(await fsPromises.readFile(readmePath, "utf8"), provider, config, projectName, packageManager);
		if (!result.applied) adaptationFailed = true;
		else await write(readmePath, result.content);
	} catch {
		adaptationFailed = true;
	}
	if (packageManager === "pnpm") await write(path.join(projectPath, "pnpm-workspace.yaml"), PNPM_WORKSPACE);
	return {
		...config,
		apiKeyWritten,
		adaptationFailed
	};
}
//#endregion
//#region src/commands/create/create.ts
const DEFAULT_TEMPLATE = {
	githubUrl: "https://github.com/mastra-ai/template-agent-harness",
	title: "Agent Harness",
	slug: "template-agent-harness",
	agents: ["agent"],
	mcp: [],
	tools: [],
	networks: [],
	workflows: []
};
var CreateCancelledError = class extends Error {
	constructor() {
		super("Operation cancelled");
		this.name = "CreateCancelledError";
	}
};
function isCreateCancelledError(error) {
	return error instanceof CreateCancelledError;
}
function getCreateCommandAnalyticsArgs(args) {
	return {
		mode: args.empty ? "empty" : args.template !== void 0 ? "template" : "managed",
		skills: args.skills,
		git: args.git
	};
}
function cancelCreate() {
	p.cancel("Operation cancelled");
	throw new CreateCancelledError();
}
async function runCreatePrompt(prompt) {
	const controller = new AbortController();
	let rejectCancellation = () => {};
	let cancellationAnnounced = false;
	const announceCancellation = () => {
		if (cancellationAnnounced) return;
		cancellationAnnounced = true;
		p.cancel("Operation cancelled");
	};
	const cancellation = new Promise((_resolve, reject) => {
		rejectCancellation = reject;
	});
	const abort = () => {
		controller.abort();
		announceCancellation();
		rejectCancellation(new CreateCancelledError());
	};
	process.once("SIGINT", abort);
	try {
		const value = await Promise.race([prompt(controller.signal), cancellation]);
		if (p.isCancel(value)) {
			announceCancellation();
			throw new CreateCancelledError();
		}
		return value;
	} finally {
		process.removeListener("SIGINT", abort);
	}
}
async function promptForProjectName() {
	return runCreatePrompt((signal) => p.text({
		message: "What do you want to name your project?",
		placeholder: "my-mastra-app",
		signal,
		validate: (value) => {
			if (!value || value.length === 0) return `Project name can't be empty`;
			if (fs.existsSync(value)) return `A directory named "${value}" already exists. Please choose a different name.`;
		}
	}));
}
async function promptForProvider() {
	return runCreatePrompt((signal) => p.select({
		message: "Select a default model provider:",
		options: [...CREATE_LLM_PROVIDERS],
		signal,
		showInstructions: false
	}));
}
async function promptForObservabilityOptIn() {
	const choice = await p.select({
		message: `Enable Mastra platform observability? (opens auth flow)`,
		options: [{
			value: "yes",
			label: "Yes"
		}, {
			value: "no",
			label: "No"
		}],
		initialValue: "yes",
		showInstructions: false
	});
	if (p.isCancel(choice)) {
		p.log.info("Skipping Mastra platform setup.");
		return false;
	}
	return choice === "yes";
}
async function promptForApiKey(provider) {
	const providerName = CREATE_LLM_PROVIDERS.find((option) => option.value === provider)?.label ?? provider;
	if (await runCreatePrompt((signal) => p.select({
		message: `Enter your ${providerName} API key?`,
		options: [{
			value: "skip",
			label: "Skip for now",
			hint: "default"
		}, {
			value: "enter",
			label: "Enter API key"
		}],
		initialValue: "skip",
		showInstructions: false,
		signal
	})) === "skip") return void 0;
	return runCreatePrompt((signal) => p.password({
		message: "Enter your API key:",
		mask: "*",
		clearOnError: true,
		validate: (value) => {
			if (!value) return `API key can't be empty`;
		},
		signal
	}));
}
function normalizeDirectCreateOptions(args) {
	return {
		projectName: args.projectName,
		empty: args.empty ?? false,
		llmProvider: args.llmProvider,
		llmApiKey: args.llmApiKey,
		skills: args.skills ?? true,
		git: args.git ?? true,
		template: args.template,
		timeout: args.timeout ?? 6e4,
		install: args.install ?? true
	};
}
async function runCreateCommand(projectName, options, dependencies = {}) {
	const normalized = normalizeCreateCommandOptions(projectName, options);
	await create({
		...normalized,
		...dependencies
	});
}
const create = async (args) => {
	const options = normalizeDirectCreateOptions(args);
	const mode = validateCreateOptionConflicts(options);
	const invocationCwd = process.cwd();
	const projectName = validateProjectName(options.projectName ?? await promptForProjectName());
	const targetPath = path.resolve(invocationCwd, projectName);
	if (fs.existsSync(targetPath)) throw new Error(`A file or directory named "${projectName}" already exists. Please choose a different name.`);
	const analytics = args.analytics ?? getAnalytics();
	let llmProvider = options.llmProvider;
	let llmApiKey = options.llmApiKey;
	let providerSelectionMethod;
	let observabilityEnabled = false;
	let platformSetupController;
	let platformSetupPromise;
	if (mode === "managed") {
		const providerProvidedByCli = llmProvider !== void 0;
		if (llmProvider) providerSelectionMethod = "cli_args";
		else {
			llmProvider = await promptForProvider();
			providerSelectionMethod = "interactive";
		}
		if (llmApiKey === void 0 && !providerProvidedByCli) llmApiKey = await promptForApiKey(llmProvider);
		if (options.projectName === void 0 || options.llmProvider === void 0) {
			observabilityEnabled = await promptForObservabilityOptIn();
			analytics?.trackEvent("cli_observability_selected", {
				command: "create",
				enabled: observabilityEnabled,
				answer: observabilityEnabled ? "yes" : "no",
				selection_method: "interactive"
			});
			if (observabilityEnabled) {
				platformSetupController = new AbortController();
				platformSetupPromise = (async () => {
					try {
						const token = await getToken(platformSetupController.signal, { skipOnInput: true });
						return {
							status: "ready",
							token,
							org: await resolveCurrentOrg(token, {
								forcePrompt: true,
								exitOnCancel: false,
								signal: platformSetupController.signal
							})
						};
					} catch (error) {
						if (error instanceof LoginCancelledError || error instanceof OrgSelectionCancelledError) return { status: "cancelled" };
						return {
							status: "failed",
							error
						};
					}
				})();
			}
		}
	}
	const selectedTemplate = mode === "empty" ? void 0 : await resolveTemplate(mode, options.template);
	analytics?.trackEvent("cli_create_mode_selected", {
		mode,
		template_slug: mode === "template" ? selectedTemplate?.slug : void 0,
		skills: options.skills,
		git: options.git
	});
	if (llmProvider) analytics?.trackEvent("cli_model_provider_selected", {
		provider: llmProvider,
		selection_method: providerSelectionMethod
	});
	if (selectedTemplate) analytics?.trackEvent("cli_template_used", { template_slug: selectedTemplate.slug });
	const versionTag = mode === "template" ? void 0 : await args.resolveVersionTag?.() ?? "latest";
	const packageManager = getPackageManager();
	const invocationIsGitWorktree = await isGitInitialized({ cwd: invocationCwd });
	const staging = await createOwnedStagingDirectory(invocationCwd, projectName);
	const materializationController = new AbortController();
	let interruptionSignal;
	const interrupt = (signal) => {
		interruptionSignal ??= signal;
		materializationController.abort();
		platformSetupController?.abort();
	};
	const handleSigint = () => interrupt("SIGINT");
	const handleSigterm = () => interrupt("SIGTERM");
	process.on("SIGINT", handleSigint);
	process.on("SIGTERM", handleSigterm);
	let selectedApiKeyEnv;
	let selectedApiKeyWritten = false;
	let materializationError;
	try {
		if (mode === "empty") {
			await writeEmptyScaffold({
				projectPath: staging.projectPath,
				projectName,
				versionTag: versionTag ?? "latest",
				packageManager
			});
			materializationController.signal.throwIfAborted();
		} else {
			const isManaged = mode === "managed";
			const branch = isManaged && versionTag === "beta" ? "beta" : void 0;
			await cloneTemplate({
				template: selectedTemplate,
				projectName,
				targetDir: staging.rootPath,
				branch,
				signal: materializationController.signal,
				...observabilityEnabled ? { silent: true } : {}
			});
			if (isManaged) {
				const providerConfig = await adaptDefaultTemplate({
					projectPath: staging.projectPath,
					projectName,
					packageManager,
					provider: llmProvider,
					apiKey: llmApiKey,
					versionTag: versionTag ?? "latest"
				});
				selectedApiKeyEnv = providerConfig.apiKeyEnv;
				selectedApiKeyWritten = providerConfig.apiKeyWritten;
				if (providerConfig.adaptationFailed) p.log.warn("Some provider setup could not be applied. Review the generated project before running it.");
				materializationController.signal.throwIfAborted();
			}
		}
		if (options.install) if (observabilityEnabled) await installDependencies(staging.projectPath, packageManager, options.timeout, materializationController.signal, true);
		else await installDependencies(staging.projectPath, packageManager, options.timeout, materializationController.signal);
		materializationController.signal.throwIfAborted();
		await publishStagedProject({
			projectPath: staging.projectPath,
			targetPath,
			projectName
		});
	} catch (error) {
		materializationError = error;
		platformSetupController?.abort();
	} finally {
		if (process.cwd() !== invocationCwd) process.chdir(invocationCwd);
		try {
			await cleanupOwnedStagingDirectory(staging.rootPath);
		} catch (cleanupError) {
			console.error(`Warning: Failed to clean up staging directory: ${cleanupError instanceof Error ? cleanupError.message : "Unknown error"}`);
		}
	}
	const platformSetup = await platformSetupPromise;
	process.removeListener("SIGINT", handleSigint);
	process.removeListener("SIGTERM", handleSigterm);
	if (interruptionSignal === "SIGINT") {
		if (observabilityEnabled) analytics?.trackEvent("cli_observability_outcome", {
			command: "create",
			outcome: "cancelled"
		});
		cancelCreate();
	}
	if (interruptionSignal === "SIGTERM") throw new Error("Operation terminated by SIGTERM");
	if (materializationError) throw materializationError;
	if (platformSetup?.status === "cancelled") {
		analytics?.trackEvent("cli_observability_outcome", {
			command: "create",
			outcome: "skipped"
		});
		p.log.info("Skipping Mastra platform setup.");
	} else if (observabilityEnabled) p.log.success(options.install ? "Default template cloned and dependencies installed." : "Default template cloned. Dependency installation was skipped.");
	const postSetup = await runPostCreateSetup({
		projectPath: targetPath,
		installSkills: options.skills,
		initializeGit: options.git,
		invocationIsGitWorktree
	});
	analytics?.trackEvent("cli_create_post_setup", {
		skills_agents: postSetup.skillsAgents,
		skills_outcome: postSetup.skillsOutcome,
		git_outcome: postSetup.gitOutcome
	});
	const setupSummary = formatPostCreateSetup(postSetup);
	if (postSetup.hasFailure) p.log.warn(setupSummary);
	else p.log.success(setupSummary);
	let observabilitySummary;
	let platformEnvWritten = false;
	let platformError = platformSetup?.status === "failed" ? platformSetup.error : void 0;
	if (platformSetup?.status === "ready") try {
		const result = await provisionObservabilityProject({
			defaultProjectName: projectName,
			mode: "create",
			token: platformSetup.token,
			org: platformSetup.org
		});
		await writeObservabilityEnv({
			projectPath: targetPath,
			token: result.token,
			projectId: result.projectId,
			endpoint: result.tracesEndpoint
		});
		platformEnvWritten = true;
		observabilitySummary = `${color.green("Mastra platform enabled.")}\n\nProject: ${color.cyan(result.projectName)} (${result.orgName})\nWrote ${color.cyan("MASTRA_PLATFORM_ACCESS_TOKEN")} and ${color.cyan("MASTRA_PROJECT_ID")} to ${color.cyan(".env")}.`;
		analytics?.trackEvent("cli_observability_outcome", {
			command: "create",
			outcome: "completed"
		});
	} catch (error) {
		platformError = error;
	}
	if (platformError !== void 0) {
		analytics?.trackEvent("cli_observability_outcome", {
			command: "create",
			outcome: "failed"
		});
		const message = platformError instanceof Error ? platformError.message : "Unknown error";
		try {
			await writeObservabilityEnv({ projectPath: targetPath });
			platformEnvWritten = true;
		} catch {}
		const placeholderSummary = platformEnvWritten ? `Empty ${color.cyan("MASTRA_PLATFORM_ACCESS_TOKEN")} and ${color.cyan("MASTRA_PROJECT_ID")} placeholders were added to your ${color.cyan(".env")} file.\n\n` : "";
		observabilitySummary = `${color.yellow("Could not connect this project to Mastra platform automatically:")} ${message}\n\n${placeholderSummary}1. Visit ${color.cyan("https://projects.mastra.ai")} to create a project and an access token.\n2. Paste the token into ${color.cyan("MASTRA_PLATFORM_ACCESS_TOKEN")} and the project id into ${color.cyan("MASTRA_PROJECT_ID")}.`;
	}
	if (mode === "managed") {
		const apiKeySummary = llmApiKey ? selectedApiKeyWritten ? `Your ${selectedApiKeyEnv} value was written to ${color.cyan(".env")}.` : `Set ${selectedApiKeyEnv} in ${color.cyan(".env")} before starting.` : platformEnvWritten ? `Set ${selectedApiKeyEnv} in ${color.cyan(".env")} before starting.` : `Copy ${color.cyan(".env.example")} to ${color.cyan(".env")} and set ${selectedApiKeyEnv} before starting.`;
		p.note(`${color.green("Success!")}\n\n${apiKeySummary}${observabilitySummary ? `\n\n${observabilitySummary}` : ""}`);
	} else if (mode === "template") p.note(`${color.green("Success!")}\n\nCreate a ${color.cyan(".env")} file if the template requires environment variables.`);
	postCreate({
		projectName,
		packageManager
	});
};
async function runPostCreateSetup({ projectPath, installSkills, initializeGit, invocationIsGitWorktree }) {
	let skillsAgents = [];
	let skillsOutcome = "opted_out";
	if (installSkills) try {
		skillsAgents = await detectCodingAgentSkills();
		skillsOutcome = (await installMastraSkills({
			directory: projectPath,
			agents: skillsAgents.map(([_, skill]) => skill)
		})).success ? "installed" : "failed";
	} catch {
		skillsOutcome = "failed";
	}
	let gitOutcome;
	if (!initializeGit) gitOutcome = "opted_out";
	else if (invocationIsGitWorktree) gitOutcome = "parent_worktree";
	else if (await isGitInitialized({ cwd: projectPath })) gitOutcome = "target_worktree";
	else try {
		await gitInit({ cwd: projectPath });
		gitOutcome = "initialized";
	} catch {
		gitOutcome = "failed";
	}
	return {
		skillsAgents: skillsAgents.map(([executable, _]) => executable),
		skillsOutcome,
		gitOutcome,
		hasFailure: skillsOutcome === "failed" || gitOutcome === "failed"
	};
}
const AGENT_MAP = {
	claude: "Claude Code",
	codex: "Codex",
	"cursor-agent": "Cursor",
	droid: "Droid",
	gemini: "Gemini",
	opencode: "OpenCode",
	pi: "Pi",
	"": "Universal"
};
function formatPostCreateSetup(result) {
	const agents = result.skillsAgents.map((agent) => AGENT_MAP[agent]).join(", ");
	return `${result.skillsOutcome === "opted_out" ? "Skipped skills (--no-skills)." : result.skillsOutcome === "installed" ? `Installed skills for ${agents}.` : `Could not install skills${agents ? ` for ${agents}` : ""}.`} ${{
		initialized: "Initialized git.",
		failed: "Could not initialize git.",
		opted_out: "Skipped git (--no-git).",
		parent_worktree: "Skipped git because the project is inside an existing worktree.",
		target_worktree: "Skipped git because the project already contains git metadata."
	}[result.gitOutcome]}`;
}
const postCreate = ({ projectName, packageManager }) => {
	p.outro(`
   ${color.green("To start your project:")}

    ${color.cyan("cd")} ${projectName}
    ${color.cyan(`${packageManager} run dev`)}
  `);
};
function parseGitHubRepositoryUrl(value) {
	try {
		const parsedUrl = new URL(value.startsWith("github.com/") ? `https://${value}` : value);
		const pathParts = parsedUrl.pathname.split("/").filter(Boolean);
		if (parsedUrl.protocol !== "https:" || parsedUrl.hostname !== "github.com" || parsedUrl.username || parsedUrl.password || parsedUrl.search || parsedUrl.hash || pathParts.length !== 2) return;
		const [owner, repoPart] = pathParts;
		const repo = repoPart?.replace(/\.git$/, "");
		if (!owner || !repo) return void 0;
		return `https://github.com/${owner}/${repo}`;
	} catch {
		return;
	}
}
function looksLikeGitHubUrl(value) {
	try {
		return new URL(value.startsWith("github.com/") ? `https://${value}` : value).hostname === "github.com";
	} catch {
		return false;
	}
}
async function validateGitHubProject(githubUrl) {
	const errors = [];
	try {
		const urlParts = new URL(githubUrl).pathname.split("/").filter(Boolean);
		const owner = urlParts[0];
		const repo = urlParts[1]?.replace(".git", "");
		if (!owner || !repo) throw new Error("Invalid GitHub URL format");
		let packageJsonContent = null;
		let indexContent = null;
		for (const branch of ["main", "master"]) try {
			const packageJsonResponse = await fetch(`https://raw.githubusercontent.com/${owner}/${repo}/${branch}/package.json`);
			if (!packageJsonResponse.ok) continue;
			packageJsonContent = await packageJsonResponse.text();
			const indexResponse = await fetch(`https://raw.githubusercontent.com/${owner}/${repo}/${branch}/src/mastra/index.ts`);
			if (indexResponse.ok) indexContent = await indexResponse.text();
			break;
		} catch {}
		if (!packageJsonContent) {
			errors.push("Could not fetch package.json from repository");
			return {
				isValid: false,
				errors
			};
		}
		try {
			const packageJson = JSON.parse(packageJsonContent);
			if (!(packageJson.dependencies?.["@mastra/core"] || packageJson.devDependencies?.["@mastra/core"] || packageJson.peerDependencies?.["@mastra/core"])) errors.push("Missing @mastra/core dependency in package.json");
		} catch {
			errors.push("Invalid package.json format");
		}
		if (!indexContent) errors.push("Missing src/mastra/index.ts file");
		else if (!indexContent.includes("export") || !indexContent.includes("new Mastra") && !indexContent.includes("Mastra(")) errors.push("src/mastra/index.ts does not export a Mastra instance");
		return {
			isValid: errors.length === 0,
			errors
		};
	} catch (error) {
		errors.push(`Failed to validate GitHub repository: ${error instanceof Error ? error.message : "Unknown error"}`);
		return {
			isValid: false,
			errors
		};
	}
}
function createFromGitHubUrl(url) {
	const urlParts = new URL(url).pathname.split("/").filter(Boolean);
	const owner = urlParts[0] || "unknown";
	const repo = urlParts[1] || "unknown";
	return {
		githubUrl: url,
		title: `${owner}/${repo}`,
		slug: repo,
		agents: [],
		mcp: [],
		tools: [],
		networks: [],
		workflows: []
	};
}
async function resolveTemplate(mode, template) {
	if (mode === "managed") return DEFAULT_TEMPLATE;
	if (template === true) {
		const templates = await loadTemplates();
		const selected = await runCreatePrompt((signal) => selectTemplate(templates, { signal }));
		if (!selected) cancelCreate();
		return selected;
	}
	if (typeof template !== "string") throw new Error("No template selected");
	const githubUrl = parseGitHubRepositoryUrl(template);
	if (githubUrl) {
		const spinner = p.spinner();
		spinner.start("Validating GitHub repository...");
		const validation = await validateGitHubProject(githubUrl);
		if (!validation.isValid) {
			spinner.stop("Validation failed");
			p.log.error("This does not appear to be a valid Mastra project:");
			validation.errors.forEach((error) => p.log.error(`  - ${error}`));
			throw new Error("Invalid Mastra project");
		}
		spinner.stop("Valid Mastra project ✓");
		return createFromGitHubUrl(githubUrl);
	}
	if (looksLikeGitHubUrl(template)) throw new Error("Invalid GitHub repository URL. Use https://github.com/<owner>/<repository>.");
	const templates = await loadTemplates();
	const found = findTemplateByName(templates, template);
	if (!found) {
		p.log.error(`Template "${template}" not found. Available templates:`);
		templates.forEach((availableTemplate) => p.log.info(`  - ${availableTemplate.title} (use: ${availableTemplate.slug.replace("template-", "")})`));
		throw new Error(`Template "${template}" not found`);
	}
	return found;
}
//#endregion
export { runCreateCommand as a, isCreateCancelledError as i, create as n, installMastraSkills as o, getCreateCommandAnalyticsArgs as r, provisionObservabilityProject as s, CreateCancelledError as t };

//# sourceMappingURL=create-DsOe2gHJ.js.map